/**
 * iter319 — Book a whole season at once.
 * Tap dates (or a weekday to grab every one of them), set the conditions once,
 * and every date becomes a session with its own RSVP window.
 */
import { useMemo, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Calendar } from './ui/calendar';
import { CalendarPlus, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const pretty = (s) => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
};
const shiftDays = (s, n) => {
  const [y, m, d] = s.split('-').map(Number);
  const dt = new Date(y, m - 1, d - n);
  return iso(dt);
};
const hhmm = (t) => {
  const [h, m] = (t || '00:00').split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, '0')} ${ampm}`;
};

export default function BulkSessionsSheet({ open, onClose, group, sessions = [], sessionDefaults, fineDefaults, onCreated }) {
  const [picked, setPicked] = useState([]);
  const [weekdays, setWeekdays] = useState([]);
  const [months, setMonths] = useState(3);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    title: 'Pickleball',
    start_time: sessionDefaults?.default_start_time || '19:00',
    num_courts: 1,
    min_players: '',
    max_players: '',
    session_fee: '',
    rsvp_open_days_before: 2,
    rsvp_open_time: '17:00',
    venue_payer: 'rotation',
  });

  const booked = useMemo(
    () => new Set((sessions || []).filter((s) => s.status !== 'cancelled').map((s) => s.date)),
    [sessions],
  );
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const applyWeekdays = () => {
    if (!weekdays.length) { toast.error('Pick at least one weekday'); return; }
    const out = new Set(picked);
    const cursor = new Date();
    cursor.setHours(0, 0, 0, 0);
    cursor.setDate(cursor.getDate() + 1);
    const end = new Date(cursor);
    end.setMonth(end.getMonth() + Number(months));
    while (cursor <= end) {
      if (weekdays.includes(cursor.getDay()) && !booked.has(iso(cursor))) out.add(iso(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    setPicked([...out].sort());
    toast.success(`${out.size} dates picked — deselect any you don't want`);
  };

  const submit = async () => {
    if (!picked.length) { toast.error('Pick at least one date'); return; }
    if (!form.title.trim()) { toast.error('Give the sessions a title'); return; }
    setBusy(true);
    try {
      const r = await api.post(`/groups/${group.id}/sessions/bulk`, {
        dates: picked,
        title: form.title.trim(),
        start_time: form.start_time,
        num_courts: Number(form.num_courts) || 1,
        min_players: form.min_players === '' ? null : Number(form.min_players),
        max_players: form.max_players === '' ? null : Number(form.max_players),
        session_fee: form.session_fee === '' ? null : Number(form.session_fee),
        rsvp_open_days_before: Number(form.rsvp_open_days_before),
        rsvp_open_time: form.rsvp_open_time,
        venue_payer: form.venue_payer,
      });
      const { created_count, skipped_count } = r.data;
      toast.success(
        `${created_count} session${created_count === 1 ? '' : 's'} created`
        + (skipped_count ? ` · ${skipped_count} skipped (already booked)` : ''),
      );
      setPicked([]);
      setWeekdays([]);
      onCreated?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create the sessions');
    } finally {
      setBusy(false);
    }
  };

  const sample = picked[0];
  const lateDay = fineDefaults?.late_cancel_day_offset ?? 1;
  const lateHour = fineDefaults?.late_cancel_cutoff_hour ?? 16;
  const lateAmount = fineDefaults?.late_cancel ?? 300;

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose?.()}>
      <SheetContent side="bottom" className="rounded-t-3xl max-h-[94vh] overflow-y-auto" data-testid="bulk-sessions-sheet">
        <SheetHeader className="text-left">
          <SheetTitle className="font-display tracking-tight">Book several sessions</SheetTitle>
        </SheetHeader>

        <div className="mt-3 space-y-4 pb-24">
          <div className="rounded-2xl border p-3 space-y-2.5" data-testid="bulk-weekday-picker">
            <p className="text-xs font-semibold">Grab every…</p>
            <div className="flex flex-wrap gap-1.5">
              {DAYS.map((d, i) => (
                <button key={d} type="button" data-testid={`bulk-weekday-${d.toLowerCase()}`}
                  onClick={() => setWeekdays((w) => (w.includes(i) ? w.filter((x) => x !== i) : [...w, i]))}
                  className={`h-8 px-3 rounded-full border text-xs font-bold transition-colors ${weekdays.includes(i) ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-muted'}`}>
                  {d}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <div className="flex gap-1" data-testid="bulk-months">
                {[1, 2, 3, 6].map((m) => (
                  <button key={m} type="button" onClick={() => setMonths(m)} data-testid={`bulk-months-${m}`}
                    className={`h-9 px-2.5 rounded-xl border text-xs font-bold transition-colors ${Number(months) === m ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-muted'}`}>
                    {m}m
                  </button>
                ))}
              </div>
              <Button size="sm" variant="outline" className="rounded-full h-9 text-xs flex-1" onClick={applyWeekdays} data-testid="bulk-apply-weekdays">
                <Sparkles className="w-3.5 h-3.5 mr-1" /> Add to calendar
              </Button>
            </div>
          </div>

          <div className="rounded-2xl border overflow-hidden" data-testid="bulk-calendar">
            <Calendar
              mode="multiple"
              selected={picked.map((s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d); })}
              onSelect={(ds) => setPicked((ds || []).map(iso).filter((d) => !booked.has(d)).sort())}
              disabled={[{ before: new Date() }, (d) => booked.has(iso(d))]}
              numberOfMonths={1}
            />
          </div>

          {picked.length > 0 && (
            <div className="rounded-2xl bg-muted/50 p-3" data-testid="bulk-selected-summary">
              <p className="text-xs font-bold">{picked.length} date{picked.length > 1 ? 's' : ''} selected</p>
              <p className="text-xs text-muted-foreground mt-1">
                {picked.slice(0, 6).map(pretty).join(' · ')}{picked.length > 6 ? ` +${picked.length - 6} more` : ''}
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2.5">
            <div className="col-span-2">
              <Label className="text-xs">Title</Label>
              <Input value={form.title} onChange={set('title')} className="h-10 rounded-xl border-2" data-testid="bulk-title" />
            </div>
            <div>
              <Label className="text-xs">Start time</Label>
              <Input type="time" value={form.start_time} onChange={set('start_time')} className="h-10 rounded-xl border-2" data-testid="bulk-start-time" />
            </div>
            <div>
              <Label className="text-xs">Courts</Label>
              <Input type="number" min="1" value={form.num_courts} onChange={set('num_courts')} className="h-10 rounded-xl border-2" data-testid="bulk-courts" />
            </div>
            <div>
              <Label className="text-xs">Min players</Label>
              <Input type="number" placeholder="auto" value={form.min_players} onChange={set('min_players')} className="h-10 rounded-xl border-2" data-testid="bulk-min-players" />
            </div>
            <div>
              <Label className="text-xs">Max players</Label>
              <Input type="number" placeholder="auto" value={form.max_players} onChange={set('max_players')} className="h-10 rounded-xl border-2" data-testid="bulk-max-players" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Fee per person (blank = group default)</Label>
              <Input type="number" placeholder="group default" value={form.session_fee} onChange={set('session_fee')} className="h-10 rounded-xl border-2" data-testid="bulk-fee" />
            </div>
          </div>

          <div className="rounded-2xl border p-3 space-y-2" data-testid="bulk-venue-payer">
            <p className="text-xs font-semibold">Who pays the court?</p>
            <div className="flex flex-wrap gap-1.5">
              {[{ id: 'rotation', label: 'Rotation / ask later' }, { id: 'group_fund', label: 'Group fund' }]
                .concat((group?.members || []).map((m) => ({ id: m.id, label: m.name?.split(' ')[0] })))
                .map((o) => (
                  <button key={o.id} type="button" data-testid={`bulk-payer-${o.id}`}
                    onClick={() => setForm((f) => ({ ...f, venue_payer: o.id }))}
                    className={`h-8 px-2.5 rounded-full border text-xs font-bold transition-colors ${form.venue_payer === o.id ? 'bg-primary text-primary-foreground border-primary' : 'hover:bg-muted'}`}>
                    {o.label}
                  </button>
                ))}
            </div>
            <p className="text-xs text-muted-foreground">
              Group fund settles every one of these straight away — you&apos;ll never get the &ldquo;Who paid?&rdquo; prompt.
            </p>
          </div>

          <div className="rounded-2xl border p-3 space-y-2.5" data-testid="bulk-rsvp-rules">
            <p className="text-xs font-semibold">When does RSVP open?</p>
            <div className="grid grid-cols-2 gap-2.5">
              <div>
                <Label className="text-xs">Days before</Label>
                <Input type="number" min="0" value={form.rsvp_open_days_before} onChange={set('rsvp_open_days_before')} className="h-10 rounded-xl border-2" data-testid="bulk-rsvp-days" />
              </div>
              <div>
                <Label className="text-xs">At</Label>
                <Input type="time" value={form.rsvp_open_time} onChange={set('rsvp_open_time')} className="h-10 rounded-xl border-2" data-testid="bulk-rsvp-time" />
              </div>
            </div>
            {sample && (
              <div className="rounded-xl bg-muted/60 p-2.5 space-y-1" data-testid="bulk-rule-preview">
                <p className="text-xs">
                  Session <span className="font-bold">{pretty(sample)} {hhmm(form.start_time)}</span> →
                  RSVP opens <span className="font-bold">{pretty(shiftDays(sample, Number(form.rsvp_open_days_before) || 0))} {hhmm(form.rsvp_open_time)}</span> and stays open till start.
                </p>
                <p className="text-xs text-muted-foreground">
                  Cancel after {pretty(shiftDays(sample, lateDay))} {hhmm(`${String(lateHour).padStart(2, '0')}:00`)} → ₹{lateAmount} late-cancel fine (change it in Session rules).
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="sticky bottom-0 -mx-6 px-6 py-3 bg-background border-t">
          <Button className="rounded-full w-full h-12" onClick={submit} disabled={busy || !picked.length} data-testid="bulk-create-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CalendarPlus className="w-4 h-4 mr-1.5" /> Create {picked.length || ''} session{picked.length === 1 ? '' : 's'}</>}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
